import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useUserData } from './UserDataContext';
import { useTheme } from './ThemeProvider';
import { Shield, Minimize2, Maximize2, X } from 'lucide-react';
import { LegionActionPanel } from './LegionActionPanel';
import { SafeStorage } from '../utils/safeStorage';
import { getDbSafe } from '../services/firebaseConfig';
import { collection, query, onSnapshot } from 'firebase/firestore';

interface Position {
    x: number;
    y: number;
}

// v2: reset once to the new top/collapsed default (old key held the previous
// bottom-right auto-saved position). Future drags persist under this key.
const STORAGE_KEY = 'legion_remote_position_v2';

// Safe-area inset at the top (notch / status bar) in px, probed from CSS env().
function safeAreaTop(): number {
    try {
        const probe = document.createElement('div');
        probe.style.cssText = 'position:fixed;top:0;height:env(safe-area-inset-top,0px);visibility:hidden;pointer-events:none;';
        document.body.appendChild(probe);
        const h = probe.getBoundingClientRect().height;
        document.body.removeChild(probe);
        return Number.isFinite(h) ? h : 0;
    } catch {
        return 0;
    }
}

// Initial spawn: near the TOP of the screen, below the safe-area inset so it
// clears the notch/status bar. The Watcher remote stacks just below this one so
// the two don't overlap for Legion mods who see both. Only used on first-ever
// appearance — a moved remote restores its saved position from storage instead.
function getDefaultPosition(): Position {
    return { x: 12, y: safeAreaTop() + 12 };
}

export function LegionRemote() {
    const { userProfile } = useUserData();
    const { theme } = useTheme();
    const [position, setPosition] = useState<Position>(getDefaultPosition);
    // Default to the compact collapsed pill (icon + name + X + expand); the user
    // taps the maximize icon to open the full remote.
    const [isMinimized, setIsMinimized] = useState(true);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 });
    const remoteRef = useRef<HTMLDivElement>(null);
    const [flaggedCount, setFlaggedCount] = useState<number>(0);
    const flagUnsubRef = useRef<(() => void) | null>(null);

    // How many accounts the nightly scan flagged and nobody has vouched for yet.
    //
    // Mirrors the Watcher remote's pending-reports badge on purpose: a moderator
    // already reads a number in that corner as "there is work", so the queue
    // announces itself the same way rather than waiting to be opened. Cleared
    // accounts are excluded — a badge that counts things already dealt with
    // teaches its reader to ignore it.
    useEffect(() => {
        const rank = String(userProfile?.rank || '').toLowerCase();
        if (!userProfile?.id || (rank !== 'watcher' && rank !== 'legion')) return;
        let active = true;
        (async () => {
            try {
                const db = await getDbSafe();
                // Every document in this collection IS an open item: marking an
                // account good deletes its flag rather than setting a field, so
                // there is nothing to filter out. A `cleared != true` filter here
                // would also have matched nothing, since Firestore cannot match a
                // document whose field is absent.
                const q = query(collection(db, 'flagged_accounts'));
                const unsub = onSnapshot(q, snap => {
                    if (active) setFlaggedCount(snap.size);
                }, () => { /* index missing or no access — badge simply stays at 0 */ });
                flagUnsubRef.current = unsub;
            } catch { /* silent */ }
        })();
        return () => {
            active = false;
            flagUnsubRef.current?.();
        };
    }, [userProfile?.id, userProfile?.rank]);

    // Deliberately NOT restoring a saved position at boot.
    //
    // The remote is meant to start in a known place every launch — top
    // left — so it is never off-screen after a rotation or a smaller
    // window, and is never hunted for. It stays freely draggable for the
    // rest of the session; the move simply does not outlive the launch.

    // Save position when it changes
    useEffect(() => {
        SafeStorage.setItem(STORAGE_KEY, JSON.stringify(position));
    }, [position]);

    const [isHidden, setIsHidden] = useState(false);

    // Dragging used to be blocked whenever the remote was expanded, so the only
    // way to move it was to collapse it first. It now drags in either state; the
    // guard is on WHAT was pressed rather than on the remote's size, so a press
    // that lands on a button, link or field still does that thing instead of
    // starting a drag.
    const isInteractive = (target: EventTarget | null): boolean =>
        !!(target as HTMLElement | null)?.closest?.('button, a, input, select, textarea, [role="button"]');

    const handleMouseDown = (e: React.MouseEvent) => {
        if (isInteractive(e.target)) return;
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - position.x,
            y: e.clientY - position.y,
        });
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        if (isInteractive(e.target)) return;
        setIsDragging(true);
        const touch = e.touches[0];
        setDragOffset({
            x: touch.clientX - position.x,
            y: touch.clientY - position.y,
        });
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;
        e.preventDefault();
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        updatePosition(newX, newY);
    };

    const handleTouchMove = (e: TouchEvent) => {
        if (!isDragging) return;
        // e.preventDefault(); // Preventing default on touchmove can interfere with scrolling if not careful, but needed for drag
        const touch = e.touches[0];
        const newX = touch.clientX - dragOffset.x;
        const newY = touch.clientY - dragOffset.y;
        updatePosition(newX, newY);
    };

    const updatePosition = (x: number, y: number) => {
        const maxX = window.innerWidth - (remoteRef.current?.offsetWidth || 280);
        const maxY = window.innerHeight - (remoteRef.current?.offsetHeight || 100);
        setPosition({
            x: Math.max(0, Math.min(x, maxX)),
            y: Math.max(0, Math.min(y, maxY)),
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            window.addEventListener('touchmove', handleTouchMove, { passive: false });
            window.addEventListener('touchend', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
                window.removeEventListener('touchmove', handleTouchMove);
                window.removeEventListener('touchend', handleMouseUp);
            };
        }
    }, [isDragging, dragOffset]);

    const toggleMinimize = () => {
        setIsMinimized(!isMinimized);
        setIsExpanded(false);
    };

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
        setIsMinimized(false);
    };

    // Only render for Legion users who are actually logged in
    if (!userProfile?.id || userProfile?.rank !== 'legion' || isHidden || typeof document === 'undefined') {
        return null;
    }

    return createPortal(
        <>
            <div
                ref={remoteRef}
                style={{
                    position: 'fixed',
                    left: `${position.x}px`,
                    top: `${position.y}px`,
                    zIndex: 9999999, // Extremely High Z-Index to stay on top
                    cursor: isDragging ? 'grabbing' : 'grab',
                    touchAction: 'none', // Prevent scrolling while dragging
                    pointerEvents: 'none' // Allow clicks to pass through container
                }}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
            >
                <div
                    className="rounded-lg shadow-2xl border-2 backdrop-blur-sm"
                    style={{
                        backgroundColor: theme === 'dark' ? 'rgba(26, 26, 26, 0.95)' : 'rgba(253, 252, 240, 0.95)',
                        borderColor: theme === 'dark' ? '#FFD700' : '#ea580c',
                        boxShadow: theme === 'dark'
                            ? '0 0 20px rgba(255, 215, 0, 0.3)'
                            : '0 0 20px rgba(234, 88, 12, 0.3)',
                        pointerEvents: 'auto' // Re-enable pointer events for the card itself
                    }}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-3 border-b" style={{
                        borderColor: theme === 'dark' ? '#FFD700' : '#ea580c',
                    }}>
                        <div className="flex items-center gap-2">
                            <Shield
                                className="w-5 h-5"
                                style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}
                            />
                            <span
                                className="font-bold text-sm"
                                style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}
                            >
                                {isMinimized ? 'Legion' : 'Legion Remote'}
                            </span>
                            {/* Accounts with irregularities awaiting review, in the
                                same corner and the same shape as the Watcher
                                remote's report badge. */}
                            {flaggedCount > 0 && (
                                <span
                                    className="inline-flex items-center justify-center rounded-full text-[10px] font-black min-w-[18px] h-[18px] px-1"
                                    style={{
                                        background: '#F39C12',
                                        color: '#fff',
                                        boxShadow: '0 0 6px rgba(243,156,18,0.7)',
                                    }}
                                    title={`${flaggedCount} account(s) with irregularities`}
                                >
                                    {flaggedCount > 99 ? '99+' : flaggedCount}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            {/* Hide Button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm('Hide Legion Remote? You will need to reload the app to see it again.')) {
                                        setIsHidden(true);
                                    }
                                }}
                                className="p-1 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
                                style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}
                                title="Hide completely (Reload to restore)"
                            >
                                <X className="w-4 h-4" />
                            </button>

                            {/* Minimize Button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleMinimize();
                                }}
                                className="p-1 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
                                style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}
                            >
                                {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>

                    {/* Body - Only show when not minimized */}
                    {!isMinimized && (
                        <div className="p-3 space-y-2">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpand();
                                }}
                                className="w-full px-4 py-2 rounded-lg font-bold text-sm transition-all hover:scale-105"
                                style={{
                                    backgroundColor: theme === 'dark' ? '#FFD700' : '#ea580c',
                                    color: theme === 'dark' ? '#000000' : '#ffffff',
                                }}
                            >
                                {isExpanded ? 'Close Panel' : 'Open Control Panel'}
                            </button>

                            <div className="text-[10px] text-center opacity-60" style={{
                                color: theme === 'dark' ? '#FFD700' : '#ea580c',
                            }}>
                                Drag to reposition
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Action Panel - Rendered separately */}
            {isExpanded && (
                <LegionActionPanel
                    onClose={() => setIsExpanded(false)}
                />
            )}
        </>,
        document.body
    );
}
